// SPDX-License-Identifier: Apache-2.0

// doc-label.tsx — HTML overlay above hovered/active doc nodes.
//
// v1 used drei's <Html /> which mounts via CSS3DObject inside the canvas.
// v2 doesn't have drei; instead we project the node's world position to
// screen coords each frame and position a Solid <div> absolutely over the
// canvas.
//
// We render at MOST two labels at a time (hovered + active when distinct),
// per the brief: "Only render label for hovered/active nodes (don't draw
// 100 labels at once)". The DocLabelLayer reads reactive accessors driven
// by VaultOrbit3D so visibility is controlled by Solid's <Show>.

import { Show, createMemo } from "solid-js";
import * as THREE from "three";
import { TINT_HEX } from "./nodes";
import type { VaultDoc } from "@tether/storage";

export interface DocLabelLayerProps {
  /** Reactive accessor — current camera (changes on resize/init). */
  camera: () => THREE.PerspectiveCamera | null;
  /** Reactive accessor — canvas size in CSS px. */
  size: () => { w: number; h: number };
  /** Reactive accessor — hovered doc + its world-space position. */
  hovered: () => { doc: VaultDoc; pos: THREE.Vector3 } | null;
  /** Reactive accessor — active doc + its world-space position. */
  active: () => { doc: VaultDoc; pos: THREE.Vector3 } | null;
  /** Reactive accessor — Claude-session satellites (Hephaestus 4). The
   *  layer renders a rose-tinted pill above each satellite's screen-space
   *  position. Optional — when missing or empty, no satellite labels
   *  render. */
  satellites?: () => Array<{ id: string; title: string; pos: THREE.Vector3 }>;
}

interface LabelView {
  id: string;
  title: string;
  hex: string;
  /** screen-space x,y in CSS px, with origin at canvas top-left */
  x: number;
  y: number;
  isActive: boolean;
  /** True if the projected point is in front of the camera. False = hide. */
  visible: boolean;
  /** Differentiates doc labels from satellite labels for CSS styling. */
  kind: "doc" | "satellite";
}

function project(
  pos: THREE.Vector3,
  camera: THREE.PerspectiveCamera,
  size: { w: number; h: number },
): { x: number; y: number; visible: boolean } {
  const v = pos.clone().project(camera);
  // z > 1 means behind camera; clamp to skip.
  if (v.z > 1 || v.z < -1) return { x: 0, y: 0, visible: false };
  return {
    x: ((v.x + 1) / 2) * size.w,
    y: ((-v.y + 1) / 2) * size.h,
    visible: true,
  };
}

export function DocLabelLayer(props: DocLabelLayerProps) {
  const labels = createMemo<LabelView[]>(() => {
    const cam = props.camera();
    const sz = props.size();
    if (!cam) return [];
    const out: LabelView[] = [];
    const hov = props.hovered();
    const act = props.active();
    if (act) {
      const p = project(act.pos, cam, sz);
      out.push({
        id: act.doc.id,
        title: act.doc.title,
        hex: TINT_HEX[act.doc.tint] ?? "#67e8f9",
        x: p.x,
        y: p.y - 22, // lift above the sphere
        isActive: true,
        visible: p.visible,
        kind: "doc",
      });
    }
    if (hov && hov.doc.id !== act?.doc.id) {
      const p = project(hov.pos, cam, sz);
      out.push({
        id: hov.doc.id,
        title: hov.doc.title,
        hex: TINT_HEX[hov.doc.tint] ?? "#67e8f9",
        x: p.x,
        y: p.y - 22,
        isActive: false,
        visible: p.visible,
        kind: "doc",
      });
    }
    // Satellite labels (Hephaestus 4) — rose-tinted pills above each Claude
    // session's orbit position. Always rendered (no hover gating; satellites
    // are sparse enough that always-on is fine — typical fleet size ≤ 8).
    const sats = props.satellites?.() ?? [];
    for (const s of sats) {
      const p = project(s.pos, cam, sz);
      out.push({
        id: `sat:${s.id}`,
        title: s.title,
        hex: "#ff7a94",
        x: p.x,
        y: p.y - 18,
        isActive: false,
        visible: p.visible,
        kind: "satellite",
      });
    }
    return out;
  });

  return (
    <Show when={labels().length > 0}>
      <div class="vault-orbit-labels" data-testid="vault-orbit-labels">
        {labels().map((lbl) => (
          <Show when={lbl.visible}>
            <div
              class="vault-orbit-label"
              classList={{
                "vault-orbit-label--active": lbl.isActive,
                "vault-orbit-label--satellite": lbl.kind === "satellite",
              }}
              style={{
                left: `${lbl.x}px`,
                top: `${lbl.y}px`,
                "--label-tint": lbl.hex,
              }}
              data-doc-id={lbl.id}
            >
              {lbl.title}
            </div>
          </Show>
        ))}
      </div>
    </Show>
  );
}
